import React, { useState } from 'react';
import {
  Container,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../firebaseconf';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useInstitution } from '../../context/InstitutionContext';
import { useAuth } from '../../context/AuthContext';
import { createUserRoleDocument } from '../../utils/authUtils';

export default function AddUser() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currentInstitution } = useInstitution();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'teacher',
    password: '',
    confirm_password: '',
    employee_id: '',
    designation: '',
    department: '',
    qualification: ''
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validation
      if (!formData.first_name.trim()) {
        throw new Error('First name is required');
      }
      if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        throw new Error('Valid email is required');
      }
      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }
      if (formData.password !== formData.confirm_password) {
        throw new Error('Passwords do not match');
      }
      if (!currentInstitution?.id) {
        throw new Error('Institution not found');
      }

      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const firebaseId = userCredential.user.uid;

      // CRITICAL: Create authoritative role document in top-level users collection (STEP 4 FIX)
      try {
        await createUserRoleDocument(
          firebaseId,
          formData.email,
          formData.role,
          currentInstitution.id,
          firebaseId,
          { name: `${formData.first_name} ${formData.last_name}` }
        );
        console.log(`✅ Created role document for ${formData.role} in users collection`);
      } catch (roleErr) {
        console.warn('⚠️ Warning: Could not create role document:', roleErr.message);
      }

      // Create user document in Firestore
      const usersCollection = collection(
        db,
        'institutions',
        currentInstitution.id,
        'users'
      );

      await addDoc(usersCollection, {
        firebase_id: firebaseId,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: 'active',
        verified: false,
        email_verified: false,
        
        // Teacher-specific fields
        ...(formData.role === 'teacher' && {
          employee_id: formData.employee_id,
          designation: formData.designation,
          department: formData.department,
          qualification: formData.qualification,
          experience_years: 0,
          subjects_teaching: []
        }),

        // Admin-specific fields
        ...(formData.role === 'admin' && {
          permissions: {
            manage_users: true,
            manage_branches: true,
            manage_subjects: true,
            manage_marks: true,
            view_analytics: true
          }
        }),

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        created_by: currentUser?.id,
        institution_id: currentInstitution.id
      });

      setSuccess(`${formData.role === 'teacher' ? 'Teacher' : 'User'} created successfully!`);
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        role: 'teacher',
        password: '',
        confirm_password: '',
        employee_id: '',
        designation: '',
        department: '',
        qualification: ''
      });

      setTimeout(() => {
        navigate('/admin-dashboard');
      }, 2000);
    } catch (err) {
      console.error('Error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Email is already in use');
      } else {
        setError(err.message || 'Failed to create user');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Add New User
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          Create a new teacher or staff account for {currentInstitution?.name}
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                >
                  <MenuItem value="teacher">Teacher</MenuItem>
                  <MenuItem value="admin">Admin/Staff</MenuItem>
                </TextField>
              </Grid>

              {formData.role === 'teacher' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Employee ID"
                      name="employee_id"
                      value={formData.employee_id}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Designation"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      placeholder="e.g., Assistant Professor"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Department"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Qualification"
                      name="qualification"
                      value={formData.qualification}
                      onChange={handleChange}
                      placeholder="e.g., M.Tech, B.Tech"
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 6 characters"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  name="confirm_password"
                  type="password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin-dashboard')}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Create User'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}
